import { type DateTime } from 'luxon';
import { z } from 'zod';

export const fieldTypeSchema = z.enum([
  'string',
  'text',
  'integer',
  'float',
  'boolean',
  'date',
  'datetime',
  'select',
  'multi_select',
  'linked_assets',
  'file',
  'slider',
  'quality_slider',
  'dropzone',
  'tag_selector',
]);

export const metadataTypeOptions = [
  {
    label: 'Single-line text',
    value: 'string',
  },
  {
    label: 'Multi-line text',
    value: 'text',
  },
  {
    label: 'Integer number',
    value: 'integer',
  },
  {
    label: 'Floating point number',
    value: 'float',
  },
  {
    label: 'Toggle',
    value: 'boolean',
  },
  {
    label: 'Date',
    value: 'date',
  },
  {
    label: 'Date and time',
    value: 'datetime',
  },
  {
    label: 'Select',
    value: 'select',
  },
  {
    label: 'Multi-select',
    value: 'multi_select',
  },
  {
    label: 'Linked assets',
    value: 'linked_assets',
  },
];

export type MetadataTypeMap = {
  string: string;
  text: string;
  integer: number;
  float: number;
  boolean: boolean;
  date: Date;
  datetime: DateTime;
  select: string;
  multi_select: Array<string>;
  linked_assets: Array<string>;
  options: object;
};

export type FieldType = z.infer<typeof fieldTypeSchema>;

/** Raw choices vary by field type, so this stays a permissive union rather than a per-type shape. */
export const metadataOptionsSchema = z.union([z.array(z.any()), z.object({ choices: z.record(z.string(), z.any()) })]);

export const metadataFieldSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().optional(),
  type: fieldTypeSchema,
  searchable: z.boolean(),
  facet: z.boolean(),
  public: z.boolean(),
  allowNewOptions: z.boolean().optional(),
  createdBy: z.object({
    id: z.string(),
    name: z.string(),
  }),
  createdAt: z.string(),
  updatedBy: z.object({
    id: z.string(),
    name: z.string(),
  }),
  updatedAt: z.string(),
  options: metadataOptionsSchema.optional(),
});

export const metadataSchema = z.object({
  metadata: z.array(metadataFieldSchema),
  pagination: z.object({
    count: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
});

export const publicMetadataFieldSchema = z.object({
  // The guest multi-select aggregation omits id; single-asset guest metadata isn't
  // schema-validated at all, so this covers both without assuming either always has it.
  id: z.string().optional(),
  slug: z.string(),
  name: z.string(),
  description: z.string().optional(),
  type: fieldTypeSchema,
  // null means the field is unset on the selected asset(s), distinct from an empty string/array.
  value: z.union([z.array(z.string()), z.string(), z.null()]),
});

export type PublicMetadataField = z.infer<typeof publicMetadataFieldSchema>;
export type MetadataField = z.infer<typeof metadataFieldSchema>;
export type Metadata = z.infer<typeof metadataSchema>;

export const metadataMap = {
  string: 'String',
  text: 'Text',
  integer: 'Integer',
  float: 'Float',
  boolean: 'Boolean',
  date: 'Date',
  datetime: 'Datetime',
  select: 'Select',
  multi_select: 'Multi-Select',
  linked_assets: 'Linked Assets',
};
